<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { useAnalysisStore } from '@/store/modules/analysis';
import {
  removeAsterisk,
  splitKeywordParts,
  splitBySentence,
  splitSentencesByDivider,
  splitCommaSeparatedWords,
} from '@/composables/splitSentence';
import PersonalPieChart from './PersonalPieChart.vue';
import PersonalityKeyword from './PersonalityKeyword.vue';

const props = defineProps({
  pData: Object,
  userName: String,
  isMobile: Boolean,
});
const store = useAnalysisStore();
// const userName = computed(() => store.userName)
onMounted(() => {
  // pData.value = store.analysisResult;
});

const myEnergy = computed(() => splitBySentence(props.pData.음양조후성향.기본특성));
const myRhythm = computed(() => splitBySentence(props.pData.음양조후성향.상세특성));
const strengthItems = computed(() => splitCommaSeparatedWords(props.pData.음양조후성향.주요강점));
const weaknessItems = computed(() => splitCommaSeparatedWords(props.pData.음양조후성향.주요약점));
const strengthDetail = computed(() => splitBySentence(props.pData.음양조후성향.강점설명));
const weaknessDetail = computed(() => splitBySentence(props.pData.음양조후성향.약점설명));
const strengthAdvice = computed(() => props.pData.음양조후성향.강점조언);
const weaknessAdvice = computed(() => props.pData.음양조후성향.약점조언);

// 음양 차트 데이터
const yinYangData = computed(() => props.pData.음양조후분석.음양비율);
const parsedYinYang = computed(() => {
  const value = parseFloat(yinYangData.value.replace('+', '').replace('-', '')) || 0;
  let yang = 0;
  let yin = 0;

  if (yinYangData.value.startsWith('+')) {
    yang = value;
    yin = 100 - yang;
  } else if (yinYangData.value.startsWith('-')) {
    yin = value;
    yang = 100 - yin;
  } else {
    yang = value;
    yin = 100 - yang;
  }
  return { yang, yin };
});
const yinYangChartData = computed(() => ({
  '음의 기운': parsedYinYang.value.yin.toFixed(1),
  '양의 기운': parsedYinYang.value.yang.toFixed(1),
}));
const yinYangLabelList = ['음의 기운', '양의 기운'];
const yinYangColorMap = {
  '양의 기운': '#EAEAEA',
  '음의 기운': '#727272',
};

// 조후 차트 데이터
const johooData = computed(() => props.pData.음양조후분석.조후비율);
const parsedJohoo = computed(() => {
  const value = parseFloat(johooData.value.replace('+', '').replace('-', '')) || 0;
  let hot = 0;
  let cold = 0;
  if (johooData.value.startsWith('+')) {
    hot = value;
    cold = 100 - hot;
  } else if (johooData.value.startsWith('-')) {
    cold = value;
    hot = 100 - cold;
  } else {
    hot = value;
    cold = 100 - hot;
  }
  return { hot, cold };
});
const johooChartData = computed(() => ({
  '뜨거운 기운': parsedJohoo.value.hot.toFixed(1),
  '차가운 기운': parsedJohoo.value.cold.toFixed(1),
}));
const johooLabelList = ['뜨거운 기운', '차가운 기운'];
const johooColorMap = {
  '뜨거운 기운': '#FFD374',
  '차가운 기운': '#75A4FD',
};
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title personality">
          <span class="detail-tit__num personality">4</span>
          <span class="detail-tit__txt personality">
            <span>음과 양의 구성으로 본&nbsp;</span>
            <span>나의 성향은?</span>
          </span>
        </div>
        <div class="personality-wrapper">
          <div class="person-content-box">
            <div class="personality-chart__wrapper" :class="{ mobile: isMobile }">
              <PersonalPieChart
                :chartData="yinYangChartData"
                :labelList="yinYangLabelList"
                :colorMap="yinYangColorMap"
                centerTitle="음/양"
                labelDirection="column"
                :isMobile="isMobile"
              />
              <PersonalPieChart
                :chartData="johooChartData"
                :labelList="johooLabelList"
                :colorMap="johooColorMap"
                centerTitle="조후"
                labelDirection="column"
                :isMobile="isMobile"
              />
            </div>
          </div>
          <div class="personality-divider" />
          <div class="person-content-box">
            <PersonalityKeyword
              :left-title="['나를 표현하는', '중심 키워드 😊']"
              :keywords="props.pData.음양조후성향.주요특징"
              :isMobile="isMobile"
            />
          </div>
          <div class="personality-divider" />

          <div class="person-content-box">
            <div class="content__title">⚡️ 음양의 구성으로 본 나의 에너지는?</div>
            <div v-for="(sentence, idx) in myEnergy" :key="idx">
              <span class="content__body-txt">
                {{ sentence }}
              </span>
            </div>
          </div>
          <div class="person-content-box">
            <div class="content__title">🎵 나만의 리듬과 균형을 찾아가는 방식</div>
            <div v-for="(sentence, idx) in myRhythm" :key="idx">
              <span class="content__body-txt">
                {{ sentence }}
              </span>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="personality-wrapper">
          <div class="person-desc-box">
            <div class="desc__title">✨ 나를 빛나게 하는 힘은 무엇일까요?</div>
            <div class="desc__body blue">
              <div class="top-keyword">
                <div class="top-keyword__box">
                  <div v-for="(item, idx) in strengthItems" :key="idx" class="keyword-item blue">
                    {{ item }}
                  </div>
                </div>
                <div v-for="(str, idx) in strengthDetail" :key="idx" class="top-keyword__desc">
                  {{ str }}
                </div>
              </div>
              <div class="bottom-white-box">
                <div class="white-box__tit">이런 방향도 생각해 보세요. 🤔</div>
                <div class="white-box__desc">
                  {{ strengthAdvice }}
                </div>
              </div>
            </div>
          </div>
          <div class="person-desc-box">
            <div class="desc__title">🌪️ 나를 흔들리게 하는 키워드들</div>
            <div class="desc__body red">
              <div class="top-keyword">
                <div class="top-keyword__box">
                  <div v-for="(item, idx) in weaknessItems" :key="idx" class="keyword-item red">
                    {{ item }}
                  </div>
                </div>
                <div v-for="(str, idx) in weaknessDetail" :key="idx" class="top-keyword__desc">
                  {{ str }}
                </div>
              </div>
              <div class="bottom-white-box">
                <div class="white-box__tit">조금 더 편안해 질 수 있는 나를 위해! 😌</div>
                <div class="white-box__desc">
                  {{ weaknessAdvice }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
